// HomesPulse listing form — compact reusable field components.
import React from "react";

export const SectionTitle = ({ children, sub }) => (
  <div className="mb-5">
    <h2 className="text-2xl font-black text-[#0F172A]">{children}</h2>
    {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
  </div>
);

export const Label = ({ children, req }) => (
  <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">
    {children}{req && <span className="ml-0.5 text-[#c8102e]">*</span>}
  </label>
);

const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none transition-colors focus:border-[#2563EB]";

export const Text = ({ label, req, value, onChange, placeholder, type = "text", testid, span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label req={req}>{label}</Label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} className={inputCls} data-testid={testid} />
  </div>
);

export const Num = ({ label, req, value, onChange, placeholder, testid, span, step, min }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label req={req}>{label}</Label>
    <input type="number" step={step} min={min} value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      placeholder={placeholder} className={inputCls} data-testid={testid} />
  </div>
);

export const Select = ({ label, req, value, onChange, options, placeholder = "Select…", testid, span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label req={req}>{label}</Label>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.v;
        const lbl = typeof o === "string" ? o : o.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </div>
);

export const TextArea = ({ label, req, value, onChange, placeholder, rows = 4, testid, span = true }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label req={req}>{label}</Label>
    <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} className={inputCls} data-testid={testid} />
  </div>
);

export const CheckGroup = ({ label, options, values = [], onToggle, testid }) => (
  <div className="sm:col-span-2">
    <Label>{label}</Label>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button type="button" key={opt} onClick={() => onToggle(opt)}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${active ? "border-[#2563EB] bg-[#EEF2FF] text-[#2563EB]" : "border-slate-300 bg-white text-slate-600 hover:border-[#2563EB]"}`}>
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

export const Radio = ({ label, req, value, onChange, options, testid }) => (
  <div className="sm:col-span-2">
    <Label req={req}>{label}</Label>
    <div className="space-y-2" data-testid={testid}>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.v;
        const lbl = typeof o === "string" ? o : o.label;
        return (
          <label key={val} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm ${value === val ? "border-[#2563EB] bg-[#EEF2FF]" : "border-slate-200 bg-white"}`}>
            <input type="radio" checked={value === val} onChange={() => onChange(val)} className="h-4 w-4 accent-[#2563EB]" />
            <span className="font-semibold text-[#0F172A]">{lbl}</span>
          </label>
        );
      })}
    </div>
  </div>
);

export const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 sm:col-span-2">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#2563EB]" data-testid={testid} />
    <span className="text-sm font-semibold text-[#0F172A]">{label}</span>
  </label>
);

export const Grid = ({ children }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
